import { CONFIG } from "../../../config";
import axios from "axios";
import { useQuery } from "@tanstack/react-query";
import { useCallback, useMemo } from "react";

const CRMCONFIG = CONFIG.modules["crm"];
const API_HOST_CRM = CRMCONFIG.host;

// Development logging utility
const isDevelopment = process.env.NODE_ENV === 'development';
const devLog = (message, data) => {
  if (isDevelopment) {
    console.log(message, data);
  }
};

/**
 * Custom hook for fetching unreached status analytics data
 * @param {Object} queries - Query parameters for the API call
 * @param {Object} options - Additional configuration options
 * @param {boolean} options.enabled - Whether the query should be enabled
 * @param {number} options.staleTime - Time in milliseconds before data is considered stale
 * @param {number} options.cacheTime - Time in milliseconds to keep data in cache
 * @param {number} options.retry - Number of retry attempts on failure
 * @param {boolean} options.refetchOnWindowFocus - Whether to refetch on window focus
 * @returns {Object} React Query result object
 */
export const useGetUnreachedStatusAnalytics = (queries = {}, options = {}) => {
  // Validate inputs
  const validatedQueries = useMemo(() => {
    if (!queries || typeof queries !== 'object') {
      devLog('[useGetUnreachedStatusAnalytics] Invalid queries parameter, using empty object');
      return {};
    }
    return queries;
  }, [queries]);

  // Default options with user overrides
  const queryOptions = useMemo(() => ({
    enabled: true,
    refetchOnWindowFocus: false,
    retry: 2,
    staleTime: 1000 * 60 * 5, // 5 minutes
    cacheTime: 1000 * 60 * 10, // 10 minutes
    ...options
  }), [options]);

  // Memoized query key for better performance
  const queryKey = useMemo(() => {
    const sortedEntries = Object.entries(validatedQueries)
      .filter(([, value]) => value !== undefined && value !== null)
      .sort(([a], [b]) => a.localeCompare(b));

    return ["unreachedStatusAnalytics", ...sortedEntries.flat()];
  }, [validatedQueries]);

  // Memoized fetch function to prevent unnecessary re-renders
  const fetchUnreachedStatusAnalytics = useCallback(async () => {
    try {
      // Filter out undefined/null values from params
      const cleanParams = Object.fromEntries(
        Object.entries(validatedQueries).filter(([, value]) =>
          value !== undefined && value !== null && value !== ''
        )
      );

      devLog('[useGetUnreachedStatusAnalytics] Making API call with params:', cleanParams);

      const response = await axios.get(
        `${API_HOST_CRM}/analytics/unreached-status-analytics`,
        {
          params: cleanParams,
          timeout: 30000, // 30 second timeout
        }
      );

      devLog('[useGetUnreachedStatusAnalytics] Backend Data:', response.data);

      // Basic response validation
      if (!response.data) {
        throw new Error('No data received from server');
      }

      return response.data;
    } catch (error) {
      // Enhanced error handling with network detection
      const isNetworkError = !error.response && error.code === 'ENOTFOUND';
      const isTimeoutError = error.code === 'ECONNABORTED';

      let errorMessage = 'Error fetching unreached status analytics';

      if (isNetworkError) {
        errorMessage += ': Network connection failed';
      } else if (isTimeoutError) {
        errorMessage += ': Request timeout';
      } else if (error.response) {
        errorMessage += `: Server responded with ${error.response.status}`;
      }

      console.error(errorMessage, {
        originalError: error,
        params: validatedQueries,
        timestamp: new Date().toISOString()
      });

      throw new Error(errorMessage);
    }
  }, [validatedQueries]);

  return useQuery({
    queryKey,
    queryFn: fetchUnreachedStatusAnalytics,
    enabled: queryOptions.enabled,
    refetchOnWindowFocus: queryOptions.refetchOnWindowFocus,
    retry: queryOptions.retry,
    staleTime: queryOptions.staleTime,
    cacheTime: queryOptions.cacheTime,
    onError: (error) => {
      console.error("Error in useGetUnreachedStatusAnalytics:", {
        error: error.message,
        queries: validatedQueries,
        timestamp: new Date().toISOString()
      });
    },
    // Performance optimization: only select data if it exists
    select: (data) => data || null,
  });
};

/**
 * Custom hook for fetching unreached status analytics data with flexible query params
 *
 * Signature: `useGetUnreachedStatusAnalyticsRange(queries = {}, options = {})`
 *
 * @param {Object} queries - Query parameters to send to the API (e.g. { from: 'YYYY-MM-DD', to: 'YYYY-MM-DD' })
 * @param {Object} options - Hook options passed to react-query (enabled, staleTime, cacheTime, retry, refetchOnWindowFocus, fetchKey)
 * @returns {Object} React Query result object
 */
export const useGetUnreachedStatusAnalyticsRange = (queries = {}, options = {}) => {
  const validatedQueries = (() => {
    if (!queries || typeof queries !== 'object') return {};
    return queries;
  })();

  const fetchUnreachedStatusAnalytics = async () => {
    try {
      const cleanParams = Object.fromEntries(
        Object.entries(validatedQueries).filter(([, value]) =>
          value !== undefined && value !== null && value !== ''
        )
      );

      devLog('[useGetUnreachedStatusAnalyticsRange] Making API call with params:', cleanParams);

      const response = await axios.get(
        `${API_HOST_CRM}/analytics/unreached-status-analytics`,
        {
          params: cleanParams,
          timeout: 30000,
        }
      );

      devLog('[useGetUnreachedStatusAnalyticsRange] Backend Data:', response.data);

      if (!response.data) throw new Error('No data received from server');

      return response.data;
    } catch (error) {
      const isNetworkError = !error.response && error.code === 'ENOTFOUND';
      const isTimeoutError = error.code === 'ECONNABORTED';

      let errorMessage = 'Error fetching unreached status analytics';

      if (isNetworkError) {
        errorMessage += ': Network connection failed';
      } else if (isTimeoutError) {
        errorMessage += ': Request timeout';
      } else if (error.response) {
        errorMessage += `: Server responded with ${error.response.status}`;
      }

      console.error(errorMessage, {
        originalError: error,
        params: validatedQueries,
        timestamp: new Date().toISOString()
      });

      throw new Error(errorMessage);
    }
  };

  const queryKey = (() => {
    const sortedEntries = Object.entries(validatedQueries)
      .filter(([, value]) => value !== undefined && value !== null)
      .sort(([a], [b]) => a.localeCompare(b));

    const base = ["unreachedStatusAnalyticsRange", ...sortedEntries.flat()];
    if (options.fetchKey) base.push(options.fetchKey);
    return base;
  })();

  return useQuery({
    queryKey,
    queryFn: fetchUnreachedStatusAnalytics,
    refetchOnWindowFocus: options.refetchOnWindowFocus ?? false,
    retry: options.retry ?? 2,
    staleTime: options.staleTime ?? 1000 * 60 * 5,
    cacheTime: options.cacheTime ?? 1000 * 60 * 10,
    enabled: options.enabled ?? true,
    onError: (error) => {
      console.error("Error in useGetUnreachedStatusAnalyticsRange:", {
        error: error.message,
        params: validatedQueries,
        timestamp: new Date().toISOString()
      });
    },
    select: (data) => data || null,
  });
};
